import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import http, { resolveApiUrl } from '../api/http';
import SimplePdfViewer from '../components/SimplePdfViewer';

const isHttpUrl = (raw) => {
  try {
    const u = new URL(String(raw || '').trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch (e) {
    return false;
  }
};

const isPdfUrl = (url, name) => {
  const n = String(name || '').toLowerCase();
  if (n.endsWith('.pdf')) return true;
  const u = String(url || '').toLowerCase();
  return /\.pdf($|\?|#)/.test(u);
};

const isPptUrl = (url, name) => {
  const n = String(name || '').toLowerCase();
  if (n.endsWith('.ppt') || n.endsWith('.pptx')) return true;
  const u = String(url || '').toLowerCase();
  return /\.pptx?($|\?|#)/.test(u);
};

const isVideoUrl = (url, name, mime) => {
  const m = String(mime || '').toLowerCase();
  if (m.startsWith('video/')) return true;
  const n = String(name || '').toLowerCase();
  if (/\.(mp4|webm|ogg|mov|m4v|m3u8)($|\?|#)/.test(n)) return true;
  const u = String(url || '').toLowerCase();
  return /\.(mp4|webm|ogg|mov|m4v|m3u8)($|\?|#)/.test(u);
};

const toAbsoluteResourceUrl = (raw) => {
  const value = String(raw || '').trim();
  if (!value) return '';
  if (isHttpUrl(value)) return value;
  if (value.startsWith('/')) return resolveApiUrl(value);
  return '';
};

const buildPptEmbedUrl = (pptUrl) => {
  const src = String(pptUrl || '').trim();
  if (!src || !isHttpUrl(src)) return '';
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(src)}`;
};

const normalizeVideoPreviewUrl = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  try {
    const u = new URL(s);
    const host = u.hostname.toLowerCase();
    if (host.includes('youtube.com')) {
      if (u.pathname.startsWith('/embed/')) return u.toString();
      const vid = u.searchParams.get('v');
      if (vid) return `https://www.youtube.com/embed/${vid}`;
    }
    if (host === 'youtu.be') {
      const vid = u.pathname.replace(/^\//, '').trim();
      if (vid) return `https://www.youtube.com/embed/${vid}`;
    }
    return u.toString();
  } catch (e) {
    return '';
  }
};

const toPositiveNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
};

const toHour2 = (seconds) => Math.round((toPositiveNumber(seconds) / 3600) * 100) / 100;

const normalizeFileName = (value) => {
  const s = String(value || '').trim();
  return s ? s : '';
};

const buildResourceLabel = (item) => {
  const type = String(item?.type || 'file');
  const name = normalizeFileName(item?.name);
  const prefix = type === 'pdf' ? 'PDF' : type === 'ppt' ? 'PPT' : type === 'video' ? '视频' : '文件';
  return name ? `${prefix}：${name}` : prefix;
};

const pickUrlByType = ({ type, urlPresigned, urlProxy }) => {
  const t = String(type || 'file');
  // PPT：Office 在线预览服务需要可公开访问的 URL（presigned 更可能可用）
  if (t === 'ppt') return String(urlPresigned || '').trim() || String(urlProxy || '').trim();
  // 其他：优先同源代理（避免 CORS/跨域下载问题）
  return String(urlProxy || '').trim() || String(urlPresigned || '').trim();
};

const isRenderableUrl = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return false;
  if (s.startsWith('/')) return true;
  if (s.startsWith('blob:') || s.startsWith('data:')) return true;
  return isHttpUrl(s);
};

const inferKind = (url, name, mime) => {
  const u = String(url || '');
  if (isVideoUrl(u, name, mime)) return 'video';
  if (isPdfUrl(u, name)) return 'pdf';
  if (isPptUrl(u, name)) return 'ppt';
  return 'file';
};

export default function TrainingLearningPage() {
  const { planId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [row, setRow] = useState(null);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [sessionId, setSessionId] = useState(null);

  const sessionIdRef = useRef(null);
  const stoppingRef = useRef(false);

  const requiredHours = useMemo(() => {
    const planned = toPositiveNumber(row?.planned_hours);
    if (planned > 0) return planned;
    return toPositiveNumber(row?.training_hours);
  }, [row]);

  const resources = useMemo(() => {
    const list = [];

    // 1) 新：文件管理教材（material_*）
    const materialPresigned = toAbsoluteResourceUrl(row?.material_url);
    const materialProxy = toAbsoluteResourceUrl(row?.material_proxy_api);
    const materialKind = row?.material_kind || inferKind(materialPresigned || materialProxy, row?.material_name, row?.material_mime);
    const materialUrl = pickUrlByType({ type: materialKind, urlPresigned: materialPresigned, urlProxy: materialProxy });
    if (isRenderableUrl(materialUrl)) {
      list.push({
        id: 'material',
        source: 'material',
        type: materialKind,
        url: materialUrl,
        name: row?.material_name || row?.attachment_name || row?.topic
      });
    }

    // 2) 兼容：旧附件（attachment_*）
    const attachmentPresigned = toAbsoluteResourceUrl(row?.attachment_url);
    const attachmentProxy = toAbsoluteResourceUrl(row?.attachment_proxy_api);
    const attachmentKind = String(row?.attachment_kind || '').trim().toLowerCase() || inferKind(attachmentPresigned || attachmentProxy, row?.attachment_name, null);
    const attachmentUrl = pickUrlByType({ type: attachmentKind, urlPresigned: attachmentPresigned, urlProxy: attachmentProxy });
    if (isRenderableUrl(attachmentUrl)) {
      list.push({
        id: 'attachment',
        source: 'attachment',
        type: attachmentKind,
        url: attachmentUrl,
        name: row?.attachment_name || row?.topic
      });
    }

    // 3) 兼容：旧视频链接（video_url）
    const videoUrl = toAbsoluteResourceUrl(row?.video_url);
    if (isRenderableUrl(videoUrl)) {
      list.push({ id: 'video', source: 'video', type: 'video', url: videoUrl, name: row?.topic });
    }

    // 去重：同 URL 不重复展示
    const seen = new Set();
    return list.filter((r) => {
      const k = String(r?.url || '');
      if (!k) return false;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [row]);

  const [activeResourceId, setActiveResourceId] = useState('');

  useEffect(() => {
    const first = resources?.[0]?.id || '';
    if (!first) {
      setActiveResourceId('');
      return;
    }
    const exists = resources.some((r) => String(r?.id) === String(activeResourceId));
    if (!activeResourceId || !exists) setActiveResourceId(first);
  }, [activeResourceId, resources]);

  const activeResource = useMemo(() => {
    if (!resources?.length) return null;
    return resources.find((r) => String(r?.id) === String(activeResourceId)) || resources[0];
  }, [activeResourceId, resources]);

  const stopStudy = useCallback(async () => {
    if (stoppingRef.current) return;
    const sid = sessionIdRef.current;
    if (!sid || !planId) return;
    stoppingRef.current = true;
    try {
      const res = await http.post(`/education-training/training-records/${planId}/study/stop`, {
        session_id: sid
      }, { __silent: true });
      const sec = Number(res?.data?.total_seconds || 0);
      setTotalSeconds(sec);
    } catch (e) {
      // ignore on close
    } finally {
      sessionIdRef.current = null;
      setSessionId(null);
      stoppingRef.current = false;
    }
  }, [planId]);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      if (!planId) {
        setError('无效的计划ID');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const rec = await http.get('/education-training/training-records');
        const list = Array.isArray(rec.data) ? rec.data : [];
        const matched = list.find((item) => Number(item?.plan_id) === Number(planId));
        if (!matched) {
          if (mounted) {
            setError('未找到该培训记录');
            setLoading(false);
          }
          return;
        }

        const start = await http.post(`/education-training/training-records/${planId}/study/start`, {});
        if (!mounted) return;

        setRow(matched);
        setTotalSeconds(Number(start?.data?.total_seconds || matched?.study_seconds || 0));
        setSessionId(Number(start?.data?.session_id || 0) || null);
        sessionIdRef.current = Number(start?.data?.session_id || 0) || null;
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || '加载学习页面失败');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, [planId]);

  useEffect(() => {
    if (!sessionId || !planId) return undefined;
    const timer = window.setInterval(async () => {
      try {
        const res = await http.post(`/education-training/training-records/${planId}/study/ping`, {
          session_id: sessionId
        }, { __silent: true });
        const sec = Number(res?.data?.total_seconds || 0);
        setTotalSeconds(sec);
      } catch (e) {
        // ignore intermittent failures
      }
    }, 15000);

    return () => window.clearInterval(timer);
  }, [planId, sessionId]);

  useEffect(() => {
    const onBeforeUnload = () => {
      stopStudy();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      stopStudy();
    };
  }, [stopStudy]);

  const goBack = async () => {
    await stopStudy();
    navigate('/education-training/records');
  };

  const canTakeExam = useMemo(() => {
    if (!row?.exam_id) return false;
    if (requiredHours <= 0) return true;
    return toHour2(totalSeconds) >= requiredHours;
  }, [requiredHours, row?.exam_id, totalSeconds]);

  const goExam = async () => {
    if (!row?.exam_id) return;
    if (!canTakeExam) return;
    await stopStudy();
    navigate(`/exam-system?examId=${Number(row.exam_id)}&planId=${Number(planId || 0)}&autoStart=1`);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>开始学习</h2>
        <button
          type="button"
          onClick={goBack}
          style={{
            padding: '.55rem 1rem',
            fontSize: 13,
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 500,
            border: '1px solid var(--border-color)',
            background: 'var(--bg-tertiary)',
            color: 'var(--text-primary)'
          }}
        >
          返回培训记录
        </button>
      </div>

      {loading ? <div>正在加载学习内容...</div> : null}
      {error ? (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#991b1b' }}>
          {error}
        </div>
      ) : null}

      {!loading && !error && row ? (
        <>
          <div style={{ background: '#fff', border: '1px solid var(--border-color)', borderRadius: 12, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{row.topic || '培训课程'}</div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
              学习学时：{toHour2(totalSeconds)} / {requiredHours || 0}
              {requiredHours > 0 && toHour2(totalSeconds) >= requiredHours ? '（已达标）' : ''}
            </div>
            {row?.exam_id ? (
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={goExam}
                  disabled={!canTakeExam}
                  style={{
                    padding: '.5rem .9rem',
                    fontSize: 13,
                    borderRadius: 6,
                    cursor: canTakeExam ? 'pointer' : 'not-allowed',
                    fontWeight: 600,
                    border: '1px solid var(--border-color)',
                    background: canTakeExam ? 'var(--bg-tertiary)' : '#f1f5f9',
                    color: canTakeExam ? 'var(--text-primary)' : '#94a3b8'
                  }}
                  title={canTakeExam ? '进入考试' : '学习学时未达标，暂不能考试'}
                >
                  进入考试
                </button>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {row.exam_title ? `关联考试：${row.exam_title}` : `关联考试ID：${row.exam_id}`}
                </span>
              </div>
            ) : null}
          </div>

          {!activeResource ? (
            <div style={{ background: '#f8fafc', border: '1px dashed var(--border-color)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#475569' }}>
              当前培训计划未配置教材（文件管理教材 / 旧附件 / 旧视频链接）。请联系管理员在培训计划中选择教材。
            </div>
          ) : (
            <>
              <div style={{ background: '#fff', border: '1px solid var(--border-color)', borderRadius: 12, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>教材列表</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  {(resources || []).map((item) => {
                    const active = String(item?.id) === String(activeResource?.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setActiveResourceId(item.id)}
                        style={{
                          padding: '.45rem .7rem',
                          fontSize: 12,
                          borderRadius: 999,
                          cursor: 'pointer',
                          border: '1px solid var(--border-color)',
                          background: active ? 'var(--bg-tertiary)' : '#fff',
                          color: 'var(--text-primary)',
                          fontWeight: active ? 700 : 500
                        }}
                        title={buildResourceLabel(item)}
                      >
                        {buildResourceLabel(item)}
                      </button>
                    );
                  })}
                </div>
                {activeResource?.url ? (
                  <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
                    <a href={activeResource.url} target="_blank" rel="noreferrer" style={{ color: 'var(--text-link, #2563eb)', fontWeight: 600 }}>
                      新窗口打开当前教材
                    </a>
                  </div>
                ) : null}
              </div>

              {activeResource?.type === 'pdf' ? (
                <SimplePdfViewer url={activeResource.url} fileName={activeResource.name || row.topic || '培训教材'} viewMode="all" />
              ) : activeResource?.type === 'ppt' ? (
                (() => {
                  const embedUrl = buildPptEmbedUrl(activeResource.url);
                  return (
                    <div style={{ display: 'grid', gap: 10 }}>
                      {embedUrl ? (
                        <div style={{ width: '100%', height: 520, borderRadius: 12, border: '1px solid var(--border-color)', overflow: 'hidden', background: '#0b1220' }}>
                          <iframe
                            title="training-learning-ppt"
                            src={embedUrl}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            allowFullScreen
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
                            referrerPolicy="no-referrer"
                          />
                        </div>
                      ) : null}
                      <div style={{ background: '#f8fafc', border: '1px solid var(--border-color)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#475569' }}>
                        <a href={activeResource.url} target="_blank" rel="noreferrer" style={{ color: 'var(--text-link, #2563eb)', fontWeight: 600 }}>
                          打开 PPT 教材
                        </a>
                        <span style={{ marginLeft: 8 }}>若页面内预览失败，请点击链接在新窗口打开。</span>
                      </div>
                    </div>
                  );
                })()
              ) : activeResource?.type === 'file' ? (
                <div style={{ background: '#f8fafc', border: '1px solid var(--border-color)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#475569' }}>
                  当前教材文件类型不支持内嵌预览，
                  <a href={activeResource.url} target="_blank" rel="noreferrer" style={{ marginLeft: 6, color: 'var(--text-link, #2563eb)', fontWeight: 600 }}>
                    点击打开教材
                  </a>
                </div>
              ) : (() => {
                const previewUrl = normalizeVideoPreviewUrl(activeResource?.url);
                const isEmbed = /youtube\.com\/embed\//i.test(previewUrl);
                return isEmbed ? (
                  <div style={{ width: '100%', height: 520, borderRadius: 12, border: '1px solid var(--border-color)', overflow: 'hidden', background: '#000' }}>
                    <iframe
                      title="training-learning-video"
                      src={previewUrl}
                      style={{ width: '100%', height: '100%', border: 'none' }}
                      allowFullScreen
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                ) : (
                  <video
                    controls
                    playsInline
                    style={{ width: '100%', borderRadius: 12, border: '1px solid var(--border-color)', background: '#000' }}
                    src={activeResource?.url}
                  />
                );
              })()}
            </>
          )}
        </>
      ) : null}
    </div>
  );
}
